import { ILegalClusterConfigType } from '../entities/IConfiguration';
import { IPlatformConfiguration } from '../entities/IPlatformConfiguration';
import { IPremiumFeatures } from '../entities/IPremiumFeatures';
import * as actionTypes from './actionTypes/configTypes';
import { action } from '../tools/reduxTools';

export const RUNTIME_CONFIG_FILE = '/config.json';
export const RUNTIME_CONFIG_API_FALLBACK = '/api/Config/GetLegalClusterConfig';

export const shouldLoadRuntimeConfigFile = (hostname: string): boolean => {
    const host = (hostname || '').split(':')[0].toLowerCase();
    return host !== 'localhost' && host !== '127.0.0.1';
};

export const isValidRuntimeConfig = (config: ILegalClusterConfigType | null | undefined): config is ILegalClusterConfigType =>
    Boolean(config?.applicationConfig?.clientID && config.applicationConfig.authority && config.applicationConfig.redirectURI);

const readJsonConfig = async (response: Response): Promise<ILegalClusterConfigType> => {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    if (!response.ok || contentType.includes('text/html')) {
        throw new Error('Runtime config file is not available');
    }

    const config = await response.json() as ILegalClusterConfigType;
    if (!isValidRuntimeConfig(config)) {
        throw new Error('Runtime config is invalid');
    }

    return config;
};

const loadConfigFromApi = (): Promise<ILegalClusterConfigType> =>
    fetch(RUNTIME_CONFIG_API_FALLBACK).then(readJsonConfig);

export const loadRuntimeConfig = (hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): Promise<ILegalClusterConfigType> => {
    if (!shouldLoadRuntimeConfigFile(hostname)) {
        return loadConfigFromApi();
    }

    return fetch(RUNTIME_CONFIG_FILE, { cache: 'no-store' })
        .then(readJsonConfig)
        .catch(() => loadConfigFromApi());
};

export const getApplicationConfig = () => action<Promise<ILegalClusterConfigType>>((dispatch) => {
    return loadRuntimeConfig().then(appConfig => {
        dispatch({
            type: actionTypes.GET_APPLICATION_CONFIG,
            appConfig
        } as IGetApplicationConfig);

        return appConfig;
    });
});

export interface IGetApplicationConfig {
    type: string,
    appConfig: ILegalClusterConfigType
}

export interface IGetPremiumConfiguration {
    type: string;
    premiumFeatures: IPremiumFeatures;
}

export interface IGetPlatformConfigurationSuccess {
    type: string;
    platformConfiguration: IPlatformConfiguration;
}

export interface IUpdatePlatformConfiguration extends IGetPlatformConfigurationSuccess {
    previousPlatformConfiguration?: IPlatformConfiguration;
}
